import React, { useState, useEffect } from 'react';
import { extracurriculars } from '../lib/supabase';
import extracurricularsData from '../data/extracurricular-activities.json';

export const ExtracurricularsPage = ({ setPage, goBack, viewerMode = 'student' }) => {
  const [activeTab, setActiveTab] = useState('school');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schoolActivities, setSchoolActivities] = useState({ categories: [] });
  const [externalActivities, setExternalActivities] = useState({ description: '', categories: [] });

  const isStudent = viewerMode === 'student';

  // Load data from Supabase with JSON fallback
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await extracurriculars.getAll();
        if (!error && data && data.length > 0) {
          // Group database data by activity_type and category
          const schoolData = data.filter(a => a.activity_type === 'school_based');
          const externalData = data.filter(a => a.activity_type === 'external');

          // Group school activities by category
          const schoolCategoriesMap = {};
          schoolData.forEach(activity => {
            const cat = activity.category || 'other';
            if (!schoolCategoriesMap[cat]) {
              schoolCategoriesMap[cat] = {
                id: cat,
                name: formatCategoryName(cat),
                activities: []
              };
            }
            schoolCategoriesMap[cat].activities.push(transformDbActivity(activity));
          });

          // Group external activities by category
          const externalCategoriesMap = {};
          externalData.forEach(activity => {
            const cat = activity.category || 'other';
            if (!externalCategoriesMap[cat]) {
              externalCategoriesMap[cat] = {
                id: cat,
                name: formatCategoryName(cat),
                programs: []
              };
            }
            externalCategoriesMap[cat].programs.push(transformDbActivity(activity));
          });

          setSchoolActivities({ categories: Object.values(schoolCategoriesMap) });
          setExternalActivities({
            description: extracurricularsData.external_activities?.description || 'Explore activities outside of school',
            categories: Object.values(externalCategoriesMap)
          });
        } else {
          // Fallback to JSON data
          setSchoolActivities(extracurricularsData.school_based_activities || { categories: [] });
          setExternalActivities(extracurricularsData.external_activities || { description: '', categories: [] });
        }
      } catch (err) {
        console.error('Error loading extracurriculars:', err);
        setSchoolActivities(extracurricularsData.school_based_activities || { categories: [] });
        setExternalActivities(extracurricularsData.external_activities || { description: '', categories: [] });
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Format category ID to readable name
  const formatCategoryName = (categoryId) => {
    const nameMap = {
      'academic-clubs': 'Academic Clubs',
      'performing-arts': 'Performing Arts',
      'sports': 'Sports',
      'service-leadership': 'Service & Leadership',
      'creative-arts': 'Creative Arts',
      'community-sports': 'Community Sports',
      'arts-culture': 'Arts & Culture',
      'stem-tech': 'STEM & Technology',
      'youth-organizations': 'Youth Organizations',
      'religious-youth': 'Religious Youth Groups'
    };
    return nameMap[categoryId] || categoryId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Transform database record to expected format
  const transformDbActivity = (item) => ({
    id: item.id,
    name: item.name,
    organization: item.organization,
    description: item.description,
    skills_developed: item.skills_developed || [],
    time_commitment: item.time_commitment,
    cost: item.cost,
    locations: item.locations,
    age_requirement: item.age_requirement,
    career_connections: item.career_connections || [],
    scholarship_potential: item.scholarship_potential,
    how_to_join: item.how_to_join,
    contact: item.contact,
    competitions: item.competitions || [],
    offerings: item.offerings || []
  });

  const tabs = [
    { id: 'school', name: 'School Activities', icon: '🏫' },
    { id: 'external', name: 'Outside School', icon: '🌍' },
    { id: 'benefits', name: 'Why It Matters', icon: '⭐' },
    { id: 'choose', name: 'How to Choose', icon: '🎯' }
  ];

  const getCategoryIcon = (categoryId) => {
    const icons = {
      'academic-clubs': '📚',
      'performing-arts': '🎭',
      'sports': '⚽',
      'service-leadership': '🤝',
      'creative-arts': '🎨',
      'community-sports': '🏃',
      'arts-culture': '🎵',
      'stem-tech': '💻',
      'youth-organizations': '👥',
      'religious-youth': '⛪'
    };
    return icons[categoryId] || '📌';
  };

  const renderActivityCard = (activity) => (
    <div
      key={activity.name}
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => setSelectedActivity(activity)}
    >
      <h4 className="font-semibold text-gray-800 mb-2">{activity.name}</h4>
      {activity.description && (
        <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
      )}

      {activity.skills_developed && (
        <div className="flex flex-wrap gap-1 mb-3">
          {activity.skills_developed.slice(0, 3).map((skill, idx) => (
            <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
              {skill}
            </span>
          ))}
        </div>
      )}

      {activity.time_commitment && (
        <div className="text-sm text-gray-500">
          <span className="mr-1">⏰</span>
          {activity.time_commitment}
        </div>
      )}

      {activity.career_connections && (
        <div className="text-sm text-gray-500 mt-1">
          <span className="mr-1">🎯</span>
          Leads to: {activity.career_connections.slice(0, 2).join(', ')}
        </div>
      )}

      <div className="mt-3 text-blue-600 text-sm font-medium">Learn more →</div>
    </div>
  );

  const renderExternalProgramCard = (program) => (
    <div
      key={program.name}
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => setSelectedActivity(program)}
    >
      <h4 className="font-semibold text-gray-800 mb-2">{program.name}</h4>
      {program.organization && (
        <p className="text-sm text-gray-500 mb-2">{program.organization}</p>
      )}
      {program.description && (
        <p className="text-sm text-gray-600 mb-3">{program.description}</p>
      )}

      {program.cost && (
        <div className="text-sm text-brand-600">
          <span className="mr-1">💰</span>
          {program.cost}
        </div>
      )}

      {program.locations && (
        <div className="text-sm text-gray-500 mt-1">
          <span className="mr-1">📍</span>
          {program.locations}
        </div>
      )}

      <div className="mt-3 text-blue-600 text-sm font-medium">Learn more →</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-brand-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-brand-700 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-3 sm:mb-4 text-cyan-100 hover:text-white flex items-center gap-2 text-sm sm:text-base"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Extracurricular Activities</h1>
          <p className="text-cyan-100 text-sm sm:text-base">
            {isStudent
              ? extracurricularsData.introduction.student_view
              : extracurricularsData.introduction.parent_view}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-lg shadow-md p-1 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedCategory(null);
                setSelectedActivity(null);
              }}
              className={`flex-1 min-w-[100px] py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Selected Activity Detail */}
        {selectedActivity && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <button
              onClick={() => setSelectedActivity(null)}
              className="mb-4 text-brand-600 hover:text-brand-800 flex items-center gap-2"
            >
              ← Back
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedActivity.name}</h2>
            {selectedActivity.organization && (
              <p className="text-gray-600 mb-4">{selectedActivity.organization}</p>
            )}
            {selectedActivity.description && (
              <p className="text-gray-700 mb-6">{selectedActivity.description}</p>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Skills */}
              {selectedActivity.skills_developed && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">Skills You'll Develop</h3>
                  <ul className="space-y-2">
                    {selectedActivity.skills_developed.map((skill, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                        {skill}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Time & Cost */}
              <div className="bg-brand-50 rounded-lg p-4">
                <h3 className="font-semibold text-brand-800 mb-3">Practical Info</h3>
                <div className="space-y-2 text-sm">
                  {selectedActivity.time_commitment && (
                    <div><strong>Time:</strong> {selectedActivity.time_commitment}</div>
                  )}
                  {selectedActivity.cost && (
                    <div><strong>Cost:</strong> {selectedActivity.cost}</div>
                  )}
                  {selectedActivity.locations && (
                    <div><strong>Where:</strong> {selectedActivity.locations}</div>
                  )}
                  {selectedActivity.age_requirement && (
                    <div><strong>Age:</strong> {selectedActivity.age_requirement}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Competitions */}
            {selectedActivity.competitions && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Competitions & Events</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {selectedActivity.competitions.map((comp, idx) => (
                    <div key={idx} className="bg-purple-50 p-3 rounded-lg">
                      <div className="font-medium text-purple-800">{comp.name}</div>
                      <div className="text-sm text-gray-600">
                        {comp.level} • {comp.when}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Career Connections */}
            {selectedActivity.career_connections && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Career Connections</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedActivity.career_connections.map((career, idx) => (
                    <span key={idx} className="bg-brand-100 text-brand-700 px-3 py-2 rounded-lg">
                      {career}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Scholarship Potential */}
            {selectedActivity.scholarship_potential && (
              <div className="mt-6 bg-yellow-50 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">Scholarship Potential</h3>
                <p className="text-gray-700">{selectedActivity.scholarship_potential}</p>
              </div>
            )}

            {/* How to Join */}
            {selectedActivity.how_to_join && (
              <div className="mt-6 bg-orange-50 rounded-lg p-4">
                <h3 className="font-semibold text-orange-800 mb-2">How to Join</h3>
                <p className="text-gray-700">{selectedActivity.how_to_join}</p>
              </div>
            )}

            {/* Contact */}
            {selectedActivity.contact && (
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2">Contact</h3>
                <p className="text-gray-700">{selectedActivity.contact}</p>
              </div>
            )}

            {/* Offerings */}
            {selectedActivity.offerings && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">What's Offered</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedActivity.offerings.map((offering, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg">
                      {offering}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading activities...</p>
          </div>
        )}

        {/* School Activities Tab */}
        {activeTab === 'school' && !selectedActivity && !loading && (
          <div className="space-y-6">
            {!selectedCategory ? (
              <>
                <h2 className="text-xl font-bold text-gray-800">School-Based Activities</h2>
                <p className="text-gray-600">Activities offered at most Jamaican schools</p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {schoolActivities.categories.map(category => (
                    <div
                      key={category.id}
                      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => setSelectedCategory(category)}
                    >
                      <div className="text-4xl mb-3">{getCategoryIcon(category.id)}</div>
                      <h3 className="font-bold text-gray-800 mb-2">{category.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {category.activities.length} activities
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {category.activities.slice(0, 2).map((act, idx) => (
                          <span key={idx} className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded">
                            {act.name}
                          </span>
                        ))}
                        {category.activities.length > 2 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            +{category.activities.length - 2} more
                          </span>
                        )}
                      </div>
                      <div className="mt-4 text-brand-600 text-sm font-medium">Explore →</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-brand-600 hover:text-brand-800 flex items-center gap-2"
                >
                  ← Back to categories
                </button>

                <div className="flex items-center gap-3 mt-4">
                  <span className="text-4xl">{getCategoryIcon(selectedCategory.id)}</span>
                  <h2 className="text-xl font-bold text-gray-800">{selectedCategory.name}</h2>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                  {selectedCategory.activities.map(activity => renderActivityCard(activity))}
                </div>
              </>
            )}
          </div>
        )}

        {/* External Activities Tab */}
        {activeTab === 'external' && !selectedActivity && !loading && (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Activities Outside School</h2>
              <p className="text-gray-600">{externalActivities.description}</p>
            </div>

            {externalActivities.categories.map(category => (
              <div key={category.id}>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span>{getCategoryIcon(category.id)}</span>
                  {category.name}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.programs.map(program => renderExternalProgramCard(program))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Benefits Tab */}
        {activeTab === 'benefits' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Why Extracurriculars Matter</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">
                    {isStudent ? 'Benefits for You' : 'Benefits for Your Child'}
                  </h3>
                  <div className="space-y-4">
                    {(isStudent
                      ? extracurricularsData.benefits_of_extracurriculars.for_students
                      : extracurricularsData.benefits_of_extracurriculars.for_parents
                    ).map((item, idx) => (
                      <div key={idx}>
                        <div className="font-medium text-gray-800">{item.benefit}</div>
                        <div className="text-sm text-gray-600 mt-1">{item.explanation}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {isStudent && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-800 mb-3">What Parents Want to Know</h3>
                    <div className="space-y-4">
                      {extracurricularsData.benefits_of_extracurriculars.for_parents.map((item, idx) => (
                        <div key={idx}>
                          <div className="font-medium text-gray-800">{item.benefit}</div>
                          <div className="text-sm text-gray-600 mt-1">{item.explanation}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">The Impact on Your Future</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">85%</div>
                  <div className="text-sm text-gray-600 mt-2">
                    of universities consider extracurriculars in admissions
                  </div>
                </div>
                <div className="text-center p-4 bg-brand-50 rounded-lg">
                  <div className="text-3xl font-bold text-brand-600">Top</div>
                  <div className="text-sm text-gray-600 mt-2">
                    scholarships require demonstrated leadership
                  </div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">90%</div>
                  <div className="text-sm text-gray-600 mt-2">
                    of employers value teamwork & leadership skills
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How to Choose Tab */}
        {activeTab === 'choose' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">How to Choose Your Activities</h2>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-800 mb-3">Questions to Ask Yourself</h3>
                <ul className="space-y-2">
                  {extracurricularsData.how_to_choose.questions_to_ask.map((question, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-blue-600">?</span>
                      {question}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-3">Common Mistakes to Avoid</h3>
                  <ul className="space-y-2">
                    {extracurricularsData.how_to_choose.common_mistakes.map((mistake, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-red-600">✕</span>
                        {mistake}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">Good Advice</h3>
                  <ul className="space-y-2">
                    {extracurricularsData.how_to_choose.advice.map((advice, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">✓</span>
                        {advice}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Building a Strong Profile</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 mb-4">
                  Universities and employers look for students who show:
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Leadership roles (captain, president, head)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Long-term commitment (not just one year)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Achievement and growth</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Balance across different areas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Community service and giving back</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">★</span>
                    <span>Genuine passion (not just resume building)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Suggested Balance</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-brand-50 rounded-lg">
                  <div className="text-2xl mb-2">📚</div>
                  <div className="font-semibold">1 Academic</div>
                  <div className="text-sm text-gray-600">Quiz team, debate, subject club</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl mb-2">⚽</div>
                  <div className="font-semibold">1 Athletic/Creative</div>
                  <div className="text-sm text-gray-600">Sport, music, art, drama</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl mb-2">🤝</div>
                  <div className="font-semibold">1 Service</div>
                  <div className="text-sm text-gray-600">Interact, Key Club, volunteering</div>
                </div>
              </div>
              <p className="text-center text-gray-600 mt-4 text-sm">
                Quality matters more than quantity. Deep involvement in 2-3 activities is better than surface participation in 10.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
